import {
  computed,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import type { ReportColumn } from '../models/column.types'
import type { ReportDataState, ReportResponse, ReportRow, ReportSpec } from '../models/report.types'
import type { ChartSpec } from '../service/chart/buildChartOptions'
import { isChartDataPointCountCompatible } from '../service/chart/visualizationCatalog'

import { useChartContractValidation } from './useChartContractValidation'
import type { UseDataVisibleChartState } from './useDataVisibleChartState'
import { useGa4Override } from './useGa4Override'

export interface UseDataVisibleChartContractOptions {
  chart: UseDataVisibleChartState
  rows: MaybeRefOrGetter<readonly ReportRow[]>
  /** Combined chart/table responses use the table's larger window; cap only the chart input. */
  rowLimit?: MaybeRefOrGetter<number | null | undefined>
  response: MaybeRefOrGetter<ReportResponse | null>
  state: MaybeRefOrGetter<ReportDataState>
  currentSpec: MaybeRefOrGetter<ReportSpec | null>
  settledSpec: MaybeRefOrGetter<ReportSpec | null>
  metricLabel: (field: string) => string
  getColumn?: (field: string) => ReportColumn | null | undefined
  formatValue?: (value: unknown, field: string, returnStr?: boolean) => string
  /** Marketing channel this surface is pinned to — a logo-less metric label borrows it. */
  channel?: string
}

export interface UseDataVisibleChartContract {
  valid: ComputedRef<boolean>
  validating: Ref<boolean>
  /** The renderer's input, not its output: option building belongs to `ChartRenderer`. */
  spec: ComputedRef<ChartSpec | null>
}

function specKey(value: ReportSpec | null): string {
  return JSON.stringify(value)
}

/** Validate only the active candidate against the response produced by its exact request. */
export function useDataVisibleChartContract(
  input: UseDataVisibleChartContractOptions,
): UseDataVisibleChartContract {
  const validation = useChartContractValidation()
  // GA4 override fields read GA4's own daily values and totals (ORP-25673).
  const ga4 = useGa4Override(input, (field) => input.getColumn?.(field))
  const chartRows = computed(() => {
    const rows = ga4.rows.value
    const limit = toValue(input.rowLimit)
    return typeof limit === 'number' ? rows.slice(0, limit) : rows
  })
  const currentResponseSettled = computed(() => {
    const state = toValue(input.state)
    return (
      (state === 'ready' || state === 'empty') &&
      specKey(toValue(input.currentSpec)) === specKey(toValue(input.settledSpec))
    )
  })

  const pointCountCompatible = computed(() => {
    const meta = input.chart.activeMeta.value
    return (
      !currentResponseSettled.value ||
      !meta ||
      isChartDataPointCountCompatible(meta, chartRows.value.length)
    )
  })

  const validationRequest = computed(() => {
    const meta = input.chart.activeMeta.value
    if (
      !currentResponseSettled.value ||
      input.chart.view.value !== 'chart' ||
      !meta ||
      !input.chart.fieldsValid.value
    )
      return null
    return {
      chart_type: meta.type,
      dimensions: input.chart.validationDimensions.value,
      metrics: input.chart.requestMetrics.value,
      time_dimensions: input.chart.validationTimeDimensions.value,
      data_point_count: chartRows.value.length,
      limit: meta.defaultLimit,
    }
  })
  const validationKey = computed(() => JSON.stringify(validationRequest.value))
  const validatedKey = ref<string | null>(null)
  const valid = computed(
    () =>
      input.chart.fieldsValid.value &&
      pointCountCompatible.value &&
      (validatedKey.value !== validationKey.value || validation.result.value?.valid !== false),
  )

  watch(
    validationKey,
    async (key) => {
      validatedKey.value = null
      const request = validationRequest.value
      if (!request) {
        validation.cancel()
        return
      }
      const result = await validation.validate(request)
      if (validationKey.value === key && result) validatedKey.value = key
    },
    { immediate: true },
  )

  const spec = computed<ChartSpec | null>(() => {
    const meta = input.chart.activeMeta.value
    if (
      !currentResponseSettled.value ||
      !toValue(input.response) ||
      !meta ||
      !input.chart.fieldsValid.value ||
      !valid.value
    )
      return null
    const dimensionFields = input.chart.requestDimensions.value
    const metricFields = input.chart.requestMetrics.value
    const timeFields = input.chart.validationTimeDimensions.value
    const columns = Object.fromEntries(
      [...new Set([...dimensionFields, ...metricFields, ...timeFields])].flatMap((field) => {
        const column = input.getColumn?.(field)
        return column ? [[field, { ...column }] as const] : []
      }),
    )
    return {
      type: meta.type,
      rows: chartRows.value,
      dimensions: dimensionFields,
      metrics: metricFields,
      timeDimensions: timeFields,
      mediaDimensions: dimensionFields.filter((field) => {
        const column = input.getColumn?.(field)
        return column?.isImage === true || column?.sub_type === 'image'
      }),
      fieldRoles: meta.fieldRoles,
      presentation: {
        maxLimit: meta.maxLimit,
        maxSeries: meta.maxSeries,
        maxSmallMultiples: meta.maxSmallMultiples,
        defaultOptions: meta.defaultOptions,
      },
      fieldValues: input.chart.fieldSelection.value,
      ...(ga4.total.value ? { total: ga4.total.value } : {}),
      ...(ga4.fields.value.length ? { ga4Fields: ga4.fields.value } : {}),
      ...(Object.keys(columns).length ? { columns } : {}),
      ...(input.formatValue ? { formatValue: input.formatValue } : {}),
      ...(input.channel ? { channel: input.channel } : {}),
      metricLabel: input.metricLabel,
    }
  })

  return { valid, validating: validation.validating, spec }
}
